import type { FormEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

const SIZE_STYLES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: "sm" | "md" | "lg" | "xl";
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  hideFooter?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormModal({
  open,
  onClose,
  title,
  description,
  size = "md",
  onSubmit,
  isSubmitting = false,
  submitLabel = "Valider",
  cancelLabel = "Annuler",
  submitDisabled = false,
  hideFooter = false,
  footer,
  children,
  className,
}: FormModalProps) {
  const header = (
    <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
      <div>
        <h3 className="font-display text-xl font-black uppercase tracking-tight text-[#003366]">
          {title}
        </h3>
        {description && <p className="mt-1 text-sm font-medium text-gray-500">{description}</p>}
      </div>
      <IconButton icon={X} label="Fermer" onClick={onClose} disabled={isSubmitting} size="sm" />
    </header>
  );

  const body = <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>;

  const footerNode = !hideFooter && (
    <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
      {footer ?? (
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <Button
            type={onSubmit ? "submit" : "button"}
            disabled={submitDisabled}
            loading={isSubmitting}
          >
            {submitLabel}
          </Button>
        </>
      )}
    </footer>
  );

  const containerClass = "flex max-h-[85dvh] flex-col";

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <Motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={isSubmitting ? undefined : onClose}
          />

          <Motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className={cn(
              "relative w-full overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl",
              SIZE_STYLES[size],
              className,
            )}
          >
            {onSubmit ? (
              <form onSubmit={onSubmit} className={containerClass}>
                {header}
                {body}
                {footerNode}
              </form>
            ) : (
              <div className={containerClass}>
                {header}
                {body}
                {footerNode}
              </div>
            )}
          </Motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
